import { useAuthStore } from '@core/stores/auth-store'
import { APIError } from '@core/utils/errors'
import { logError } from '@core/utils/logger'
import { captureException } from '@sentry/react'
import Axios from 'axios'
import { t } from 'i18next'
import qs from 'qs'
import { toast } from 'sonner'

declare module 'axios' {
  interface AxiosRequestConfig {
    meta?: {
      suppressToastForStatuses?: number[]
    }
  }
}

const baseConfig = {
  baseURL: import.meta.env.VITE_API_URL,
  paramsSerializer: (params: Record<string, unknown>) =>
    qs.stringify(params, { arrayFormat: 'repeat' }),
}

export const apiInstanceWithoutInterceptors = Axios.create(baseConfig)

export const apiInstance = Axios.create(baseConfig)

// Configurable 401 handler - apps should call configureApi to set this
let onUnauthorized: (() => void) | null = null

/**
 * Configure the API instance with app-specific settings
 * @param config.onUnauthorized - Callback to handle 401 responses (e.g., redirect to logout)
 * @param config.language - Language for Accept-Language header
 */
export function configureApi(config: {
  onUnauthorized?: () => void
  language?: string
}) {
  if (config.onUnauthorized) {
    onUnauthorized = config.onUnauthorized
  }
  if (config.language) {
    apiInstance.defaults.headers['Accept-Language'] = config.language
  }
}

export function handleUnauthorized() {
  if (!onUnauthorized || !useAuthStore.getState().isLogged()) return
  onUnauthorized()
}

apiInstance.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().getAccessToken()

    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`

    return config
  },
  (error) => Promise.reject(error)
)

const statusMessages = {
  403: { message: 'Forbidden', translation: 'api.errors.forbidden' },
  404: { message: 'Not Found', translation: 'api.errors.notFound' },
  429: {
    message: 'Too Many Requests',
    translation: 'api.errors.tooManyRequests',
  },
} as const

type HandledStatus = keyof typeof statusMessages

const isHandledStatus = (status: number): status is HandledStatus =>
  status in statusMessages

export function buildErrorMessage(
  status: number,
  detail?: string,
  url?: string
) {
  const statusText = isHandledStatus(status)
    ? statusMessages[status].message
    : 'Error'
  return `${status} ${statusText}${detail ? `: ${detail}` : ''} (${url ?? 'unknown'})`
}

function toastStatusError(status: number) {
  if (isHandledStatus(status))
    toast.error(t(statusMessages[status].translation), {
      id: `api-error-${status}`,
    })
}

apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleUnauthorized()

    const status = error.response?.status
    if (status != null) {
      const suppressedStatuses = error.config?.meta?.suppressToastForStatuses
      const isSuppressed = suppressedStatuses?.includes(status)

      // For suppressed statuses, resolve with null instead of rejecting
      // so callers don't need try/catch for expected error codes
      if (isSuppressed) {
        return { ...error.response, data: null }
      }

      const apiError = new APIError(
        buildErrorMessage(
          status,
          error.response?.data?.detail,
          error.config?.url
        )
      )

      toastStatusError(status)
      if (status !== 401) {
        captureException(apiError)
        logError(apiError)
      }
    }

    return Promise.reject(error)
  }
)
